import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { onboardingService } from '../services/onboardingService';
import { useAuth } from '../contexts/AuthContext';
import '../styles/CustomersList.css';
import '../styles/Pages.css';
import '../styles/InsightsPage.css';
import '../styles/OnboardingInvoicing.css';

type TabKey = 'invoices' | 'services';

/* ── Models ──────────────────────────────────────────────── */
type InvoiceStatus = 'open' | 'paid' | 'overdue' | 'draft';
const STATUS_STYLE: Record<InvoiceStatus, { bg: string; fg: string; label: string }> = {
  open:    { bg: '#cffafe', fg: '#0e7490', label: 'Open' },
  paid:    { bg: '#d1fae5', fg: '#047857', label: 'Paid' },
  overdue: { bg: '#fee2e2', fg: '#b91c1c', label: 'Overdue' },
  draft:   { bg: '#f1f5f9', fg: '#475569', label: 'Draft' },
};

interface Invoice {
  id: string;
  projectTask: string | null;
  recipientName: string;
  recipientEmail: string;
  number: string;
  amount: number;
  created: string;
  due: string | null;
  status: InvoiceStatus;
}

interface ServicePrice { id: string; amount: number; }
interface Service {
  id: string;
  name: string;
  description?: string;
  prices: ServicePrice[];
}

const uid = () => Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-3);

const SEED_INVOICES: Invoice[] = [
  {
    id: 'inv-1', projectTask: null,
    recipientName: 'Sample Client', recipientEmail: 'sample-client-di1u42ar@example.com',
    number: 'DRAFT', amount: 500, created: 'Apr 30, 2026', due: null, status: 'open',
  },
];

const money = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const initials = (name: string) => name.trim().split(/\s+/).slice(0, 2).map(s => s[0]).join('').toUpperCase() || '?';

/* ── Add New Service modal ───────────────────────────────── */
const AddServiceModal: React.FC<{ editing: Service | null; onClose: () => void; onSave: (s: Service) => void }> = ({ editing, onClose, onSave }) => {
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [prices, setPrices] = useState<ServicePrice[]>(editing?.prices?.length ? editing.prices : [{ id: uid(), amount: 0 }]);

  const setPrice = (id: string, amount: number) => setPrices(prev => prev.map(p => p.id === id ? { ...p, amount } : p));
  const addPrice = () => setPrices(prev => [...prev, { id: uid(), amount: 0 }]);
  const removePrice = (id: string) => setPrices(prev => prev.length > 1 ? prev.filter(p => p.id !== id) : prev);

  const canSave = name.trim().length > 0;
  const save = () => {
    if (!canSave) return;
    onSave({ id: editing?.id ?? uid(), name: name.trim(), description: description.trim() || undefined, prices });
  };

  return (
    <div className="onbi-modal-backdrop" onClick={onClose}>
      <div className="onbi-modal" onClick={e => e.stopPropagation()}>
        <div className="onbi-modal-head">
          <h3>{editing ? 'Edit Service' : 'Add New Service'}</h3>
          <button type="button" className="onbi-modal-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="onbi-modal-body">
          <div className="onbi-field">
            <label className="onbi-label">Name <span className="onbi-req">*</span></label>
            <input className="onbi-input" value={name} onChange={e => setName(e.target.value)} autoFocus />
          </div>

          <div className="onbi-field">
            <label className="onbi-label">Description <span className="onbi-muted">(optional)</span></label>
            <textarea className="onbi-input onbi-textarea" rows={4} value={description} onChange={e => setDescription(e.target.value)} />
          </div>

          <div className="onbi-field">
            <label className="onbi-label">Prices</label>
            {prices.map(p => (
              <div key={p.id} className="onbi-price-row">
                <div className="onbi-price-input">
                  <span className="onbi-price-prefix">$</span>
                  <input
                    type="number" min={0} step="0.01"
                    value={p.amount === 0 ? '' : p.amount}
                    placeholder="0"
                    onChange={e => setPrice(p.id, e.target.value === '' ? 0 : Number(e.target.value))}
                  />
                </div>
                {prices.length > 1 && (
                  <button type="button" className="onbi-price-remove" title="Remove price" onClick={() => removePrice(p.id)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="onbi-add-price" onClick={addPrice}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
              Add Price
            </button>
          </div>
        </div>

        <div className="onbi-modal-foot">
          <button type="button" className="onbi-btn-cancel" onClick={onClose}>Cancel</button>
          <button type="button" className="onbi-btn-save" onClick={save} disabled={!canSave}>{editing ? 'Save Service' : 'Add Service'}</button>
        </div>
      </div>
    </div>
  );
};

/* ── Page ────────────────────────────────────────────────── */
const OnboardingInvoicing: React.FC = () => {
  const { currentOrg } = useAuth();
  const [activeTab, setActiveTab] = useState<TabKey>('invoices');
  const [search, setSearch] = useState('');
  const [invoices] = useState<Invoice[]>(SEED_INVOICES);
  const [services, setServices] = useState<Service[]>([]);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);

  // Load services from the DB (org-scoped via RLS).
  useEffect(() => {
    if (!currentOrg?.id) return;
    onboardingService.listServices()
      .then(rows => setServices(rows as Service[]))
      .catch(err => console.error('Failed to load services:', err));
  }, [currentOrg?.id]);

  const q = search.trim().toLowerCase();
  const filteredInvoices = useMemo(
    () => invoices.filter(i => !q || i.recipientName.toLowerCase().includes(q) || i.number.toLowerCase().includes(q)),
    [invoices, q],
  );
  const filteredServices = useMemo(
    () => services.filter(s => !q || s.name.toLowerCase().includes(q)),
    [services, q],
  );

  const saveService = async (s: Service) => {
    setShowServiceModal(false);
    setEditingService(null);
    try {
      const saved = await onboardingService.saveService(s) as Service;
      setServices(prev => prev.some(x => x.id === s.id) ? prev.map(x => x.id === s.id ? saved : x) : [...prev, saved]);
    } catch (err: any) {
      console.error('Failed to save service:', err);
      alert(`Failed to save service: ${err?.message ?? err}`);
    }
  };

  const deleteService = async (id: string) => {
    setServices(prev => prev.filter(x => x.id !== id));
    try { await onboardingService.deleteService(id); }
    catch (err) { console.error('Failed to delete service:', err); }
  };

  return (
    <Layout>
      <div className="customers-list-container">
        {/* Header: tabs left, search + add right */}
        <div className="customers-header-row">
          <div className="insights-tabs" role="tablist">
            <button role="tab" aria-selected={activeTab === 'invoices'} className={`insights-tab ${activeTab === 'invoices' ? 'active' : ''}`} onClick={() => setActiveTab('invoices')}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={activeTab === 'invoices' ? '#4338ca' : '#6b7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></svg>
              <span>Invoices</span>
              <span className="insights-tab-count">{invoices.length}</span>
            </button>
            <button role="tab" aria-selected={activeTab === 'services'} className={`insights-tab ${activeTab === 'services' ? 'active' : ''}`} onClick={() => setActiveTab('services')}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={activeTab === 'services' ? '#4338ca' : '#6b7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
              <span>Services</span>
              <span className="insights-tab-count">{services.length}</span>
            </button>
          </div>

          <div className="header-right-section">
            <div className="search-bar">
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input type="text" placeholder={activeTab === 'invoices' ? 'Search invoices...' : 'Search services...'} value={search} onChange={e => setSearch(e.target.value)} className="search-input" />
            </div>
            {activeTab === 'invoices' ? (
              <button className="customers-add-btn btn-primary-cta" title="Create a new invoice">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                New Invoice
              </button>
            ) : (
              <button className="customers-add-btn btn-primary-cta" title="Add a new service" onClick={() => { setEditingService(null); setShowServiceModal(true); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add Service
              </button>
            )}
          </div>
        </div>

        {activeTab === 'invoices' ? (
          <div className="onbi-wrap">
            <div className="onbi-row onbi-head">
              <span className="onbi-c-pt">Project/Task</span>
              <span className="onbi-c-rec">Recipient</span>
              <span className="onbi-c-num">Invoice #</span>
              <span className="onbi-c-amt">Amount</span>
              <span className="onbi-c-cre">Created</span>
              <span className="onbi-c-due">Due</span>
              <span className="onbi-c-sta">Status</span>
              <span className="onbi-c-act">Actions</span>
            </div>
            {filteredInvoices.map(inv => {
              const st = STATUS_STYLE[inv.status];
              return (
                <div key={inv.id} className="onbi-row">
                  <span className="onbi-c-pt onbi-muted-cell">{inv.projectTask ?? '—'}</span>
                  <span className="onbi-c-rec onbi-recipient">
                    <span className="onbi-avatar">{initials(inv.recipientName)}</span>
                    <span className="onbi-recipient-text">
                      <span className="onbi-recipient-name">{inv.recipientName}</span>
                      <span className="onbi-recipient-email">{inv.recipientEmail}</span>
                    </span>
                  </span>
                  <span className="onbi-c-num onbi-num">{inv.number}</span>
                  <span className="onbi-c-amt onbi-amt">{money(inv.amount)}</span>
                  <span className="onbi-c-cre onbi-link">{inv.created}</span>
                  <span className="onbi-c-due onbi-muted-cell">{inv.due ?? ''}</span>
                  <span className="onbi-c-sta">
                    <span className="onbi-status" style={{ background: st.bg, color: st.fg }}>{st.label}</span>
                  </span>
                  <span className="onbi-c-act">
                    <button type="button" className="onbi-more" title="More">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/></svg>
                    </button>
                  </span>
                </div>
              );
            })}
            {filteredInvoices.length === 0 && <div className="onbi-empty">No invoices found.</div>}
          </div>
        ) : (
          <div className="onbi-wrap">
            <div className="onbi-row onbi-srow onbi-head">
              <span className="onbi-sc-name">Name</span>
              <span className="onbi-sc-desc">Description</span>
              <span className="onbi-sc-price">Price</span>
              <span className="onbi-sc-act">Actions</span>
            </div>
            {filteredServices.map(s => (
              <div key={s.id} className="onbi-row onbi-srow">
                <span className="onbi-sc-name onbi-num">{s.name}</span>
                <span className="onbi-sc-desc onbi-muted-cell">{s.description || '—'}</span>
                <span className="onbi-sc-price onbi-amt">{s.prices.map(p => money(p.amount)).join(', ')}</span>
                <span className="onbi-sc-act">
                  <button type="button" className="onbi-icon-btn" title="Edit" onClick={() => { setEditingService(s); setShowServiceModal(true); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>
                  <button type="button" className="onbi-icon-btn onbi-icon-danger" title="Delete" onClick={() => deleteService(s.id)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                  </button>
                </span>
              </div>
            ))}
            {filteredServices.length === 0 && (
              <div className="onbi-empty">No services yet. Click “Add Service” to create one.</div>
            )}
          </div>
        )}
      </div>

      {showServiceModal && (
        <AddServiceModal
          editing={editingService}
          onClose={() => { setShowServiceModal(false); setEditingService(null); }}
          onSave={saveService}
        />
      )}
    </Layout>
  );
};

export default OnboardingInvoicing;
